import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import {
  CallScribeActiveTranscriptCallSelector,
  CallScribeAnsweredAtForCallSelector,
} from 'store/callScribe/selectors';
import { observeAnsweredAt } from 'store/callScribe';
import { getCallScribeCall } from 'services/callScribe';
import { CallScribeCallStatus } from 'services/callScribe/types';

export const ANSWERED_POLL_MS = 3000;
export const ANSWERED_POLL_MAX_FETCHES = 200;

const SETTLED_STATUSES: readonly CallScribeCallStatus[] = [
  CallScribeCallStatus.Completed,
  CallScribeCallStatus.Failed,
  CallScribeCallStatus.NoAnswer,
  CallScribeCallStatus.Busy,
  CallScribeCallStatus.Cancelled,
  CallScribeCallStatus.Unknown,
];

export const useAnsweredAtFallback = (): void => {
  const dispatch = useAppDispatch();
  const callId = useAppSelector(CallScribeActiveTranscriptCallSelector);
  const answeredAtUtc = useAppSelector(CallScribeAnsweredAtForCallSelector(callId));
  const generationRef = useRef(0);

  useEffect(() => {
    if (typeof callId !== 'number' || answeredAtUtc !== undefined) {
      return undefined;
    }

    generationRef.current += 1;
    const generation = generationRef.current;
    let fetched = 0;

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      if (fetched >= ANSWERED_POLL_MAX_FETCHES) {
        window.clearInterval(interval);
        return;
      }
      fetched += 1;

      void (async () => {
        const res = await getCallScribeCall(callId).catch(() => undefined);
        if (generation !== generationRef.current) {
          return;
        }
        const row = res?.data;
        if (row === undefined || row === null) {
          return;
        }
        if (typeof row.answeredAtUtc === 'string' && row.answeredAtUtc.length > 0) {
          dispatch(observeAnsweredAt({ callId, answeredAtUtc: row.answeredAtUtc }));
          window.clearInterval(interval);
          return;
        }
        if (SETTLED_STATUSES.includes(row.status)) {
          window.clearInterval(interval);
        }
      })();
    }, ANSWERED_POLL_MS);

    return () => {
      generationRef.current += 1;
      window.clearInterval(interval);
    };
  }, [callId, answeredAtUtc, dispatch]);
};
